import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import pMap from 'p-map';
import { useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';

import CourseMultiSelect from '@/components/CourseMultiSelect/CourseMultiSelect';
import { FormErrorMessage } from '@/components/FormErrorMessage';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
import { supabase } from '@/lib/supabase';
import type { Course, User } from '@/lib/types';
import { uniqueByCourseId } from '@/lib/utils';
import { userDefinedRoles } from '@/Shared/Data/StaticData';

interface UserModalProps {
	triggerCreateModal(): void;
	modalActive: boolean;
	deptChairList: User[];
}

interface FormValues {
	firstName: string;
	lastName: string;
	email: string;
	role: string;
	deptChairId: any;
}

const CONCURRENT_PROMISES = 3;

const CreateUserModal = ({ modalActive, triggerCreateModal, deptChairList }: UserModalProps) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
		control,
	} = useForm<FormValues>();
	const queryClient = useQueryClient();
	const schoolId = import.meta.env.VITE_SCHOOL_ID;
	const isMobile = useIsBreakpoint('max', 768);

	const [userCourses, setUserCourses] = useState<Pick<Course, 'course_id' | 'course_name'>[]>([]);
	const [isCreating, setIsCreating] = useState(false);
	const [pendingEmailData, setPendingEmailData] = useState<{
		email: string;
		firstName: string;
		lastName: string;
	} | null>(null);
	const [isSendingEmail, setIsSendingEmail] = useState(false);

	const createDeptList = () => {
		const formattedList = deptChairList.map((c) => ({
			label: `${c.user_first ?? ''} ${c.user_last ?? ''}`,
			value: `${c.user_first ?? ''} ${c.user_last ?? ''}`,
			id: c.user_firebase_id,
		}));
		return formattedList;
	};

	const handleModalClose = () => {
		if (!isCreating) {
			setPendingEmailData(null);
			triggerCreateModal();
		}
	};

	const isEmailUnique = (email: string) => {
		const previousUserData = queryClient.getQueryData(['get-users', schoolId]) as Partial<User[]>;
		const isEmailUnique = previousUserData.some((c) => c?.user_email === email.trim());
		return isEmailUnique;
	};

	const getValidSession = async () => {
		const {
			data: { session },
		} = await supabase.auth.getSession();

		if (session?.access_token) return session;

		const { data: refreshData } = await supabase.auth.refreshSession();
		return refreshData.session;
	};

	const handleResendEmail = async () => {
		if (!pendingEmailData) return;

		setIsSendingEmail(true);
		try {
			const session = await getValidSession();
			if (!session?.access_token) {
				showErrorToast('Your session has expired. Please sign out and sign in again.');
				setIsSendingEmail(false);
				return;
			}

			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-welcome-email`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${session.access_token}`,
					},
					body: JSON.stringify({
						email: pendingEmailData.email,
						firstName: pendingEmailData.firstName,
						lastName: pendingEmailData.lastName,
						appOrigin: window.location.origin,
					}),
				}
			);

			if (!response.ok) {
				throw new Error('Welcome email request failed');
			}

			setPendingEmailData(null);
			showSuccessToast('Welcome email sent successfully.');
		} catch (error) {
			console.error('Failed to resend welcome email:', error);
			Sentry.captureException(error);
			showErrorToast('Email failed to send. Please try again.');
		}
		setIsSendingEmail(false);
	};

	const onSubmit: SubmitHandler<FormValues> = async (formData) => {
		setIsCreating(true);
		const { email, firstName, lastName, role, deptChairId } = formData;

		interface InsertedUser {
			insert_users: {
				returning: User[];
			};
			update_users: {
				returning: User[];
			};
		}

		interface EdgeFunctionResponse {
			data: { userData: InsertedUser; welcomeEmailSent: boolean };
		}

		const session = await getValidSession();
		if (!session?.access_token) {
			showErrorToast('Your session has expired. Please sign out and sign in again.');
			setIsCreating(false);
			return;
		}

		let userData: InsertedUser;
		let welcomeEmailSent: boolean;
		try {
			const tempId = crypto.randomUUID();
			const edgeFunctionResponse = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user-from-ui`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${session.access_token}`,
					},
					body: JSON.stringify({
						id: tempId,
						firstName: firstName.trim(),
						lastName: lastName.trim(),
						email: email.trim(),
						role,
						deptChair: deptChairId || undefined,
						school_id: schoolId,
						appOrigin: window.location.origin,
					}),
				}
			);

			if (!edgeFunctionResponse.ok) {
				const errorData = await edgeFunctionResponse.json().catch(() => ({}));
				throw new Error(errorData.error || 'Failed to create user');
			}

			const edgeFunctionData = (await edgeFunctionResponse.json()) as EdgeFunctionResponse;
			userData = edgeFunctionData.data.userData;
			welcomeEmailSent = edgeFunctionData.data.welcomeEmailSent === true;
		} catch (error: any) {
			console.error('Failed to create user:', error);
			Sentry.captureException(error);

			if (
				error.message?.includes('EMAIL_EXISTS') ||
				error.message?.includes('already registered') ||
				error.message?.includes('already in use')
			) {
				showErrorToast('This email is already in use.');
			} else {
				showErrorToast('Failed to create user. Please try again.');
			}
			setIsCreating(false);
			return;
		}

		if (!welcomeEmailSent) {
			setPendingEmailData({
				email: email.trim(),
				firstName: firstName.trim(),
				lastName: lastName.trim(),
			});
			showErrorToast('User was created, but the welcome email failed to send.');
		}

		try {
			let userId: string;
			if (userData.insert_users) {
				userId = userData.insert_users.returning[0].user_id;
			} else {
				userId = userData.update_users.returning[0].user_id;
			}

			const uniqueCourses = uniqueByCourseId(userCourses);
			if (uniqueCourses.length > 0) {
				await pMap(
					uniqueCourses,
					async (course) => {
						if (course.course_id) {
							const { error } = await supabase.from('courses_users').insert({
								course_id: course.course_id,
								user_id: userId,
							});

							if (error) throw error;
						}
					},
					{ concurrency: CONCURRENT_PROMISES }
				);
			}
		} catch (error) {
			console.error('Failed to assign courses:', error);
			Sentry.captureException(error);
			showErrorToast('User was created, but some course assignments failed.');
		}

		queryClient.invalidateQueries({ queryKey: ['get-users'] });
		queryClient.invalidateQueries({ queryKey: ['get-dept-chairs'] });
		showSuccessToast('User Added');
		setIsCreating(false);
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) handleModalClose();
	};

	const formContent = (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<Controller
				control={control}
				name="role"
				rules={{ required: 'A role is required' }}
				render={({ field }) => (
					<Select value={field.value} onValueChange={field.onChange}>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Select Role" />
						</SelectTrigger>
						<SelectContent>
							{userDefinedRoles.map((role) => (
								<SelectItem key={role.value} value={role.value}>
									{role.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			/>
			<FormErrorMessage
				className="-mt-3"
				isShowing={!!errors?.role}
				message={errors.role?.message}
			/>
			<Controller
				control={control}
				name="deptChairId"
				render={({ field }) => (
					<Select
						value={field.value}
						onValueChange={(val) => field.onChange(val === '__none__' ? '' : val)}
					>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Choose Dept. Chair" />
						</SelectTrigger>
						<SelectContent className="max-h-[200px] overflow-y-auto">
							<SelectItem value="__none__">None</SelectItem>
							{createDeptList().map((chair) => (
								<SelectItem key={chair.id} value={chair.id}>
									{chair.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			/>
			<Input
				type="text"
				placeholder="First Name"
				{...register('firstName', {
					required: 'First name is required',
				})}
			/>
			<FormErrorMessage
				className="-mt-3"
				isShowing={!!errors?.firstName}
				message={errors.firstName?.message}
			/>
			<Input
				type="text"
				placeholder="Last Name"
				{...register('lastName', {
					required: 'Last name is required',
				})}
			/>
			<FormErrorMessage
				className="-mt-3"
				isShowing={!!errors?.lastName}
				message={errors.lastName?.message}
			/>
			<Input
				type="text"
				placeholder="Email"
				{...register('email', {
					required: 'Email is required',
					pattern: {
						value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,6}(?:\s+)*$/i,
						message: 'Invalid email address',
					},
					validate: (value) => {
						if (isEmailUnique(value)) {
							return 'This email is already taken';
						}
						return true;
					},
				})}
			/>
			<FormErrorMessage
				className="-mt-3"
				isShowing={!!errors?.email}
				message={errors.email?.message}
			/>
			<div>
				<p className="text-sm font-medium pb-2">Assigned Courses</p>
				<CourseMultiSelect selectedCourses={userCourses} onChange={setUserCourses} />
				<div className="pb-4" />
			</div>
		</form>
	);

	const footerButtons = (
		<div className="flex gap-2 justify-end">
			{pendingEmailData && (
				<Button
					isLoading={isSendingEmail}
					disabled={isSendingEmail}
					variant="secondary"
					onClick={handleResendEmail}
				>
					Resend Welcome Email
				</Button>
			)}
			<Button
				isLoading={isCreating}
				disabled={isCreating}
				type="submit"
				onClick={handleSubmit(onSubmit)}
			>
				Add User
			</Button>
		</div>
	);

	if (isMobile) {
		return (
			<Drawer open={modalActive} onOpenChange={handleOpenChange}>
				<DrawerContent className="max-h-[85vh]">
					<DrawerHeader>
						<DrawerTitle className="pb-2">Add New User</DrawerTitle>
						<DrawerDescription className="text-base">
							Fill in the details and assign courses.
						</DrawerDescription>
					</DrawerHeader>
					<div className="px-4 pb-2 flex-1 overflow-y-auto">{formContent}</div>
					<DrawerFooter>{footerButtons}</DrawerFooter>
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Dialog open={modalActive} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-[700px]">
				<DialogHeader>
					<DialogTitle className="pb-2">Add New User</DialogTitle>
					<DialogDescription className="text-base">
						Fill in the details and assign courses.
					</DialogDescription>
				</DialogHeader>
				<div className="max-h-[70vh] overflow-y-auto">{formContent}</div>
				<div className="flex justify-end border-t pt-4">{footerButtons}</div>
			</DialogContent>
		</Dialog>
	);
};

export default CreateUserModal;
